import React from 'react';

import PropTypes from 'prop-types';

import { Body } from '@iris/react-components/Body';
import { Box } from '@iris/react-components/Box';
import { Button } from '@iris/react-components/Button';
import { Card } from '@iris/react-components/Card';
import { Heading } from '@iris/react-components/Heading';
import { Link } from '@iris/react-components/Link';
import clsx from 'clsx';

import {
    AnimatedButton,
    Glyphicon,
} from 'reaxl';

/**
 *  The PromoCard component renders a card with either an icon/image and an optional button
 */
function PromoCard({
    animatedButton = false,
    animatedButtonState = '',
    bodyClassName,
    buttonVariant = 'secondary',
    buttonStyle = {},
    buttonText = '',
    children,
    className,
    handleButtonClick = () => { },
    heading,
    header,
    href,
    iconClassnames = '',
    iconName = '',
    imgHeight = '',
    imgWidth = '',
    imgSrc = '',
    inputComponent,
    panelClassName,
    panelBodyClassName,
    subText = '',
    uiContext = 'default',
    panelSubHeadingClassName = 'margin-vertical-4',
    subHeadingLevel = 2, // Default to h2
    ...rest
}) {

    const isSpotlightCard = uiContext === 'spotlight';

    // Map uiContext to Card variant
    const getCardVariant = () => {
        if (uiContext === 'wallet') return 'brand';
        if (uiContext === 'spotlight') return 'brand-subtle';
        return 'standard';
    };

    const imgClassNames = clsx({
        'img-responsive center-block': !imgHeight,
    }, 'text-center');

    const renderIconOrImage = () => {
        if (imgSrc) {
            return (
                <Box
                    style={{
                        width: imgWidth,
                        height: imgHeight,
                    }}
                    className="text-center"
                >
                    <img
                        height={imgHeight}
                        width={imgWidth}
                        src={imgSrc}
                        alt={heading || 'Promo card image'}
                        className={imgClassNames}
                    />
                </Box>
            );
        }

        if (iconName) {
            return (
                <Glyphicon
                    className={iconClassnames}
                    glyph={iconName}
                />
            );
        }

        return null;
    };

    const renderButton = () => {
        const buttonProps = {
            onClick: handleButtonClick,
            style: buttonStyle,
            variant: buttonVariant,
            className: 'margin-top-5',
        };

        if (animatedButton) {
            return (
                <AnimatedButton
                    buttonState={animatedButtonState}
                    buttonText={buttonText}
                    key="animated-button"
                    {...buttonProps}
                />
            );
        }

        if (href) {
            return (
                <Link
                    key="text-button"
                    {...buttonProps}
                    href={href}
                >
                    {buttonText}
                </Link>
            );
        }

        return (
            <Button
                key="text-button"
                {...buttonProps}
            >
                {buttonText}
            </Button>
        );
    };

    const bodyWrapper = (body) => (
        <Box
            className={bodyClassName}
        >
            {body}
        </Box>
    );

    const renderBody = () => {
        if (subText && typeof subText !== 'string') {
            return bodyWrapper(subText);
        }

        // If subText is a string, render it in a Body component
        if (subText && typeof subText === 'string') {
            return bodyWrapper(
                <Body
                    className="margin-top-4"
                    size="md"
                    as="p"
                >
                    {subText}
                </Body>
            );
        }

        // Fallback to children if no subText is provided
        return bodyWrapper(
            <Body
                className="margin-top-4"
                size="md"
                as="p"
            >
                {children}
            </Body>
        );
    };

    const renderItemCard = () => {
        const iconOrImage = renderIconOrImage();

        return (
            <div className="display-flex flex-column align-items-center">
                {iconOrImage && (
                    <Box className="margin-bottom-3">
                        {iconOrImage}
                    </Box>
                )}
                {heading && (
                    <Heading
                        as={`h${subHeadingLevel}`}
                        size="md"
                        className={clsx(panelSubHeadingClassName, 'margin-bottom-3')}
                    >
                        {heading}
                    </Heading>
                )}
                <Box className="width-100 padding-horizontal-6">
                    {renderBody()}
                </Box>
                {buttonText && (
                    <Box className="margin-top-3">
                        {renderButton()}
                    </Box>
                )}
            </div>
        );
    };

    return (
        <Box
            className={className}
            data-cmp="cntnr-promo-card"
            {...rest}
        >
            <Card
                variant={getCardVariant()}
                className={clsx(panelClassName, {
                    'inventory-listing': !isSpotlightCard,
                    'display-flex flex-column justify-content-center align-items-center': !header,
                })}
            >
                <Card.Content>
                    {header && (
                        <Box className="display-flex justify-content-center">
                            {header}
                        </Box>
                    )}
                    <Box className={clsx(panelBodyClassName, 'text-center')}>
                        {renderItemCard()}
                    </Box>
                </Card.Content>
            </Card>
        </Box>
    );
}

PromoCard.propTypes = {
    /**
     * Whether to show animated button (now shows spinner for loading state)
     */
    animatedButton: PropTypes.bool,
    /**
     * State of animated button ('loading' shows spinner, other states show regular button)
     */
    animatedButtonState: PropTypes.string,
    /**
     * classnames passed to the content body wrapper
     */
    bodyClassName: PropTypes.string,
    /**
     * Additional content to render alongside subText
     */
    children: PropTypes.node,
    /**
     * CSS class name for the root container
     */
    className: PropTypes.string,
    /**
     * text to be button content
     */
    buttonText: PropTypes.string,
    /**
     * styles passed to the button
     */
    buttonStyle: PropTypes.object,
    /**
     * Button variant (maps to @iris Button variant)
     */
    buttonVariant: PropTypes.oneOf(['primary', 'secondary']),
    /**
     * Click handler for the CTA
     */
    handleButtonClick: PropTypes.func,
    /**
     * text to be displayed in Heading component
     */
    heading: PropTypes.string,
    /**
     * fragment to be displayed in Card header
     */
    header: PropTypes.node,
    /**
     * URL for link buttons
     */
    href: PropTypes.string,
    /**
     * name of the icon/glyph (will be migrated to @iris/react-icons)
     */
    iconName: PropTypes.string,
    /**
     * CSS classes for the icon
     */
    iconClassnames: PropTypes.string,
    /**
     * height of the image
     */
    imgHeight: PropTypes.string,
    /**
     * width of the image
     */
    imgWidth: PropTypes.string,
    /**
     * image source
     */
    imgSrc: PropTypes.string,
    /**
     * classnames passed to the Card component
     */
    panelClassName: PropTypes.string,
    /**
     * classnames passed to the Card body container
     */
    panelBodyClassName: PropTypes.string,
    /**
     * classnames passed to the heading component
     */
    panelSubHeadingClassName: PropTypes.string,
    /**
     * Text content for the body
     */
    subText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     * Heading level (1-6) for semantic HTML
     */
    subHeadingLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    /**
     * UI context that affects card styling
     */
    uiContext: PropTypes.oneOf(['default', 'spotlight', 'wallet']),
};

export default PromoCard;
